import { api } from "./client";

export interface ClinicSettings {
  name: string;
  legal_name: string | null;
  inn: string | null;
  license_number: string | null;
  address: string | null;
  phone: string | null;
  logo_object_key: string | null;
  default_locale: string;
  default_timezone: string;
  card_prefix: string;
}

export interface SettingsRead {
  clinic: ClinicSettings;
  version: string;
}

export async function getSettings(): Promise<SettingsRead> {
  const { data } = await api.get<SettingsRead>("/settings");
  return data;
}

export async function updateSettings(
  payload: Partial<ClinicSettings>,
): Promise<SettingsRead> {
  const { data } = await api.patch<SettingsRead>("/settings", payload);
  return data;
}

export async function uploadLogo(file: File): Promise<{ logo_object_key: string }> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<{ logo_object_key: string }>("/settings/logo", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export interface AuditRow {
  id: string;
  clinic_id: string;
  actor_id: string | null;
  actor_role: string | null;
  actor_specialty: string | null;
  machine_id: string | null;
  ip_address: string | null;
  permission: string;
  verb: string;
  target_type: string;
  target_id: string | null;
  diff_keys: string[] | null;
  trace_id: string | null;
  created_at: string;
}

export async function listAudit(params: {
  permission?: string;
  target_type?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: AuditRow[]; total: number }> {
  const { data } = await api.get("/audit", { params });
  return data;
}

export async function checkAuditIntegrity(): Promise<{
  ok: boolean;
  rows_checked?: number;
  broken_at_index?: number;
}> {
  const { data } = await api.get("/audit/integrity");
  return data;
}

export interface BackupRead {
  id: string;
  clinic_id: string;
  kind: string;
  object_key: string;
  size_bytes: number;
  sha256: string;
  created_by_user_id: string | null;
  restored_at: string | null;
  created_at: string;
}

export async function listBackups(): Promise<{ items: BackupRead[]; total: number }> {
  const { data } = await api.get("/backups");
  return data;
}

export async function createBackup(): Promise<BackupRead> {
  const { data } = await api.post<BackupRead>("/backups");
  return data;
}

export async function requestRestore(backup_id: string): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>("/restore-approvals", { backup_id });
  return data;
}

export async function confirmRestore(
  approval_id: string,
  password: string,
): Promise<void> {
  await api.post(`/restore-approvals/${approval_id}/confirm`, { password });
}
